import {
    Spotlight,
    SpotlightActionData,
    SpotlightFilterFunction
} from "@mantine/spotlight";
import {useCallback, useContext, useMemo, useRef, useState} from "react";
import {useDebouncedCallback} from "@mantine/hooks";
import OmniFetchTS from "@/util/OmniFetchTS.tsx";
import {
    IconBuilding,
    IconExternalLink,
    IconFolder, IconFolderQuestion,
    IconLayout,
    IconLink,
    IconSearch,
    IconTable,
    IconUser,
    IconZoomQuestion
} from "@tabler/icons-react";
import {ISpotlightResponse} from "@/types/api/Spotlight.ts";
import {useNavigate} from "react-router-dom";
import {OmniContext} from "@/OmniContext.ts";
import getInternalLinks from "@/util/InternalLinks.tsx";
import {Group, Loader, Title} from "@mantine/core";
import {ProjectStatus} from "@/types/enums/ProjectStatus.tsx";

const customSpotlightFilter: SpotlightFilterFunction = (_query, data) => {
    return data
};

export default function OmniSpotlight() {
    const context = useContext(OmniContext)
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false)
    const internalLinks = useMemo(() => {
        if (context.user) {
            return getInternalLinks(context.user)
        }
        return []
    }, [context])
    const [actions, setActions] = useState<SpotlightActionData[]>([])
    const scrollAreaRef = useRef<HTMLDivElement | null>(null)
    const updateSpotlightOptions = useDebouncedCallback((query: string) => {
        OmniFetchTS<ISpotlightResponse>(`/api/v1/spotlight?query=${query}`).then((json) => {
            if (json.success && json.data) {
                let newActions: SpotlightActionData[] = [];
                for (const link of internalLinks) {
                    if (link.name.toLowerCase().includes(query))  {
                        newActions.push({
                            id: `${link.id}`,
                            label: link.name,
                            description: `Links > Services on ${context.environment?.siteName}`,
                            onClick: () => {
                                navigate(link.href)
                            },
                            leftSection: <IconLink/>
                        })
                    }
                }
                for (const action of json.data.links) {
                    newActions.push({
                        id: `link${action.id}`,
                        label: action.name,
                        description: `Links > ${action.category_name}`,
                        onClick: () => {
                            if (action.href.startsWith("http")) {
                                window.open(action.href, "_blank")
                            } else {
                                navigate(action.href)
                            }
                        },
                        leftSection: action.href.startsWith("http") ? <IconExternalLink/> : <IconLink/>
                    })
                }
                for (const user of json.data.users) {
                    newActions.push({
                        id: `user${user.id}`,
                        label: `${user.name} (${user.username})`,
                        description: "Users",
                        onClick: () => {
                            navigate(`/users/${user.id}`)
                        },
                        leftSection: <IconUser/>
                    })
                }
                for (const team of json.data.teams) {
                    newActions.push({
                        id: `team${team.id}`,
                        label: `${team.name} Scorecard`,
                        description: `Scorecards`,
                        onClick: () => {
                            navigate(`/scorecard/teams/${team.id}`)
                        },
                        leftSection: <IconTable/>
                    })
                    if (team.vto_enabled) {
                        newActions.push({
                            id: `teamVTO${team.id}`,
                            label: `${team.name} VTO`,
                            description: `Vision/Traction Organizers`,
                            onClick: () => {
                                navigate(`/vto/${team.id}/traction?version=published`)
                            },
                            leftSection: <IconLayout/>
                        })
                    }
                }
                for (const customer of json.data.customers) {
                    newActions.push({
                        id: `customer${customer.id}`,
                        label: `${customer.number ? `${customer.number}: ` : ""}${customer.name}`,
                        description: "Customers",
                        onClick: () => {
                            if (customer.number) {
                                navigate(`/customers/${customer.number}`)
                            } else {
                                navigate(`/customers/id/${customer.id}`)
                            }
                        },
                        leftSection: <IconBuilding/>
                    })
                }
                for (const project of json.data.projects) {
                    if (project.status == ProjectStatus.OPPORTUNITY) {
                        newActions.push({
                            id: `project${project.id}`,
                            label: `Planning: ${project.name}`,
                            description: "Projects",
                            onClick: () => {
                                navigate(`/projects/${project.id}`)
                            },
                            leftSection: <IconFolderQuestion/>
                        })
                    } else {
                        newActions.push({
                            id: `project${project.id}`,
                            label: `${project.number}: ${project.name}`,
                            description: `Projects > ${project.customer_name}`,
                            onClick: () => {
                                navigate(`/projects/${project.id}`)
                            },
                            leftSection: <IconFolder/>
                        })
                    }
                }
                setActions(newActions)
                setLoading(false)
                setTimeout(() => {
                    if (scrollAreaRef.current) {
                        let i = 0
                        for (let action of scrollAreaRef.current.querySelectorAll("[data-action=\"true\"]")) {
                            if (i == 0) {
                                action.setAttribute("data-selected", "true")
                            } else {
                                action.removeAttribute("data-selected")
                            }
                            i++
                        }
                    }
                }, 5)
            }
        })
    }, {delay: 500})

    const prepareUpdateSpotlightOptions = useCallback((query: string) => {
        setLoading(true)
        updateSpotlightOptions(query)
    }, [updateSpotlightOptions])

    return <Spotlight
        actions={actions}
        onQueryChange={prepareUpdateSpotlightOptions}
        highlightQuery={true}
        limit={50}
        scrollable={true}
        nothingFound={!loading && <Group justify={"center"}><IconZoomQuestion size={"3em"}/><Title order={2}>No Results Found</Title></Group>}
        searchProps={{
            leftSection: <IconSearch/>,
            placeholder: "Search Omni...",
            rightSection: loading && <Loader size={"sm"}/>
        }}
        filter={customSpotlightFilter}
        scrollAreaProps={{ref: scrollAreaRef}}
    />
}